import { showErrorMessage } from './UIUtils';
import * as Errors from './ErrorMessages';
import { DynaStripesCurrentNetworkName, DynaStripesCurrentNetworkCurrencySymbol } from './Constants';
import { switchToCurrentNetwork } from './BlockchainAPI';

/**
 * Dig the wallet's own JSON-RPC error code out of whatever ethers wrapped it in.
 *
 * ethers v6 re-throws an unmapped provider error as `makeError("could not
 * coalesce error", "UNKNOWN_ERROR", { error, payload })`, and makeError does
 * `Object.assign(error, info)` -- so the real `{ code: -32002 }` ends up at
 * `err.error`, while `err.code` is the useless string "UNKNOWN_ERROR". Other
 * paths nest it under `info.error` or `data.originalError`.
 */
export function extractRpcErrorCode(err: any): number | null {
  const seen = new Set<any>();
  const queue: any[] = [err];

  while (queue.length > 0) {
    const candidate = queue.shift();
    if (!candidate || typeof candidate !== 'object' || seen.has(candidate)) {
      continue;
    }
    seen.add(candidate);

    if (typeof candidate.code === 'number') {
      return candidate.code;
    }

    queue.push(candidate.error, candidate.info, candidate.data, candidate.cause, candidate.originalError);
  }

  return null;
}

/**
 * Did the user dismiss the wallet prompt?
 *
 * Checked several ways because it has to hold for any wallet: ethers maps what
 * it recognises to ACTION_REJECTED, wallets themselves use 4001, and anything
 * ethers can't map arrives as UNKNOWN_ERROR with only the text to go on.
 */
export function isUserRejection(err: any): boolean {
  return err?.code === 'ACTION_REJECTED'
    || err?.reason === 'rejected'
    || extractRpcErrorCode(err) === 4001
    || /user (rejected|denied|cancelled|canceled)/i.test(err?.message ?? '');
}

/** Keep raw calldata / revert blobs from filling the screen. */
function briefly(text: string | undefined, max = 160): string {
  if (!text) {
    return '';
  }
  return text.length > max ? text.slice(0, max).trimEnd() + '\u2026' : text;
}

export function handleError(err: any) {
  const rpcCode = extractRpcErrorCode(err);
  console.log('[handleError] Handling error ' + (rpcCode ?? err?.code) + ': ' + err?.message);

  if (isUserRejection(err)) {
    // ethers tells us what was being asked, so we can name it.
    const rejectedTransaction = err?.action === 'sendTransaction' || err?.action === 'signTransaction';
    showErrorMessage(rejectedTransaction
      ? 'You cancelled the transaction. :-('
      : 'You cancelled the request. :-(');
  } else if (rpcCode === -32002) {
    // -32002: a connection request is already queued in the wallet.
    showErrorMessage('Your wallet already has a pending connection request. Open the wallet extension and approve or dismiss it, then try again.');
  } else if (rpcCode === -32603) {
    // -32603 is the catch-all wallets return when they can't process or
    // simulate a request; the nested message is the only useful part.
    if (err?.data?.message != null) {
      showErrorMessage('Your wallet could not process the request: ' + briefly(err.data.message));
    } else {
      showErrorMessage("Your wallet couldn't process this transaction. Try again, or use a different wallet.");
    }
  } else if (err?.code === 'INSUFFICIENT_FUNDS') {
    showErrorMessage("You don't have enough " + DynaStripesCurrentNetworkCurrencySymbol + " in this account to cover the gas fee. Read the 'How to' guide for info on topping up.");
  } else if (err?.message === Errors.DS_NO_ETH_WALLET) {
    showErrorMessage("No crypto wallet detected. Please install MetaMask. Read the 'How to' guide for more info.");
  } else if (err?.message === Errors.DS_NO_ETH_ACCOUNT) {
    showErrorMessage("You need to connect an account via your crypto wallet before you can do that. Read the 'How to' guide for more info.");
  } else if (err?.message === Errors.DS_WALLET_SELECTION_REQUIRED) {
    showErrorMessage("You have more than one wallet installed. Please choose which one to use.");
  } else if (err?.message === Errors.DS_WRONG_ETH_NETWORK) {

    const errorMessage = "You're on the wrong network. Tap to switch to " + DynaStripesCurrentNetworkName + ", or read the 'How to' guide for more info.";
    const onClose = switchToCurrentNetwork;

    showErrorMessage(errorMessage, onClose);

  } else if (rpcCode != null) {
    showErrorMessage('An error occurred: (' + rpcCode + ') ' + briefly(err?.shortMessage ?? err?.message));
  } else if (err?.shortMessage || err?.message) {
    showErrorMessage('An error occurred: ' + briefly(err.shortMessage ?? err.message));
  } else {
    showErrorMessage('An error occurred.');
  }
}
